import React, { useEffect, useLayoutEffect, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { AnalyticsRouter, initGA } from './analytics';
import { Container } from "reactstrap";
import Loading from "./components/Loading";
import NavBar from "./components/NavBar";
import { useAuth0 } from "@auth0/auth0-react";
import history from "./utils/history";
import FooterUpsell from "./components/FooterUpsell";
import Footer from "./components/Footer";
import AppleRouteFallback from "./views/AppleRouteFallback";
import AppleTvSideNav from "./views/AppleTvSideNav";
import { isAppleDetailPath, scrollAppleSurfaceToTop } from "./views/appleScroll";
import {
  appleRoute,
  isAppleHomePath,
  isApplePrimaryHost,
  isAppleSurfacePath,
  isAppleTopChromePath,
} from "./views/appleTvHost";
import {
  attemptChunkReload,
  isChunkLoadFailure,
  lazyWithReload,
} from "./utils/chunkReload";
import {
  applyAppleTheme,
  ensureAppleThemeDefault,
  subscribeAppleSystemTheme,
} from "./views/appleTheme";

// styles
import "./App.css";
// fontawesome
import initFontAwesome from "./utils/initFontAwesome";

initFontAwesome();

// Catches route render errors (including ChunkLoadError after a failed reload)
// so the user sees a recover button instead of a black page.
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      isChunkError: isChunkLoadFailure(error),
    };
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, isChunkError: false });
    }
  }

  componentDidCatch(error) {
    if (isChunkLoadFailure(error) && attemptChunkReload()) {
      return;
    }
    // eslint-disable-next-line no-console
    console.error("[App] route render error:", error);
  }

  render() {
    if (this.state.hasError) {
      const { isChunkError } = this.state;
      return (
        <div style={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          color: "#fff",
          background: "#000",
          textAlign: "center",
          padding: 24,
        }}>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            {isChunkError ? "This page needs a refresh" : "Something went wrong"}
          </div>
          <div style={{ opacity: 0.7, maxWidth: 420 }}>
            {isChunkError
              ? "The site updated since you opened it. Reload to get the latest version."
              : "This section could not be displayed. Reload the page or try again in a moment."}
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: "10px 22px",
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.3)",
              background: "rgba(255,255,255,0.12)",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Every route component is lazy-loaded so the initial bundle stays small and
// each page ships only its own code. These used to be static imports, which
// bundled the ENTIRE app (~1.7 MB) into main.js — so a visitor landing on
// /apple downloaded every unrelated page (Spotify, YouTube, the original
// movie app, etc.) and parsed it before anything rendered. With lazy routes,
// main.js holds just the router shell + shared chrome, and each route streams
// its own chunk on demand.
const Home = lazyWithReload(() => import("./views/Home"));
const Profile = lazyWithReload(() => import("./views/Profile"));
const Profile2 = lazyWithReload(() => import("./views/Profile2"));
const Explore = lazyWithReload(() => import("./views/Explore"));
const Spotify = lazyWithReload(() => import("./views/Spotify"));
const YouTube = lazyWithReload(() => import("./views/YouTube"));
const Privacy = lazyWithReload(() => import("./views/Privacy"));
const Contact = lazyWithReload(() => import("./views/Contact"));
const CuratorDev = lazyWithReload(() => import("./views/CuratorDev"));
const MovieDataTest = lazyWithReload(() => import("./views/MovieDataTest"));
const CuratorMonitor = lazyWithReload(() => import("./views/CuratorMonitor"));
const CuratorSearch = lazyWithReload(() => import("./views/CuratorSearch"));
const LetterboxdTest = lazyWithReload(() => import("./views/LetterboxdTest"));
const LetterboxdList = lazyWithReload(() => import("./views/LetterboxdList"));
const LetterboxdCsvImport = lazyWithReload(() => import("./views/LetterboxdCsvImport"));
const Library = lazyWithReload(() => import("./views/Library"));
const DeveloperApi = lazyWithReload(() => import("./views/DeveloperApi"));
const GmailClonePage = lazyWithReload(() => import("./views/GmailClonePage"));
const GmailMoviePage = lazyWithReload(() => import("./views/GmailMoviePage"));
const HomeScroller = lazyWithReload(() => import("./components/HomeScroller"));
const MoviePair = lazyWithReload(() => import("./components/MoviePair"));
const MovieDetailPage = lazyWithReload(() => import("./components/MovieDetailPage"));
const ListMenu = lazyWithReload(() => import("./components/ListMenu"));
const ListDetail = lazyWithReload(() => import("./components/ListDetail"));

const AppleTvClone = lazyWithReload(() => import("./views/AppleTvClone"));
const AppleTvReels = lazyWithReload(() => import("./views/AppleTvReels"));
const AppleTvTvHome = lazyWithReload(() => import("./views/AppleTvTvHome"));
const AppleTvListPage = lazyWithReload(() => import("./views/AppleTvListPage"));
const AppleTvSearch = lazyWithReload(() => import("./views/AppleTvSearch"));
const AppleTvMapPage = lazyWithReload(() => import("./views/AppleTvMapPage"));
const AppleTvDetailPage = lazyWithReload(() => import("./views/AppleTvDetailPage"));
const AppleTvPersonPage = lazyWithReload(() => import("./views/AppleTvPersonPage"));
const AppleTvCastCrewPage = lazyWithReload(() => import("./views/AppleTvCastCrewPage"));
const AppleTvLibraryPage = lazyWithReload(() => import("./views/AppleTvLibraryPage"));
const AppleTvAccountPage = lazyWithReload(() => import("./views/AppleTvAccountPage"));
const AppleTvUserFilmsProfilePage = lazyWithReload(() => import("./views/AppleTvUserFilmsProfilePage"));
const AppleTvNewsroomPage = lazyWithReload(() => import("./views/AppleTvNewsroomPage"));
const AppleTvTeaPage = lazyWithReload(() => import("./views/AppleTvTeaPage"));

function AppleLegacyRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/apple/, "") || "/";
  return <Navigate to={`${rest}${location.search}${location.hash}`} replace />;
}

function appleTvRouteElements(basePath = "/apple") {
  const homePath = basePath || "/";
  const route = (suffix) => {
    if (!suffix) return homePath;
    return `${basePath}${suffix}`;
  };

  return [
    <Route key="apple-home" path={homePath} element={<Suspense fallback={<AppleRouteFallback variant="home" />}><AppleTvClone /></Suspense>} />,
    <Route key="apple-tv" path={route("/tv")} element={<Suspense fallback={<AppleRouteFallback variant="home" />}><AppleTvTvHome /></Suspense>} />,
    <Route key="apple-reels" path={route("/reels")} element={<Suspense fallback={<AppleRouteFallback variant="home" />}><AppleTvReels /></Suspense>} />,
    <Route key="apple-search" path={route("/search")} element={<Suspense fallback={<AppleRouteFallback variant="search" />}><AppleTvSearch /></Suspense>} />,
    <Route key="apple-map" path={route("/map")} element={<Suspense fallback={<AppleRouteFallback variant="home" />}><AppleTvMapPage /></Suspense>} />,
    <Route key="apple-library" path={route("/library")} element={<Suspense fallback={<AppleRouteFallback variant="library" />}><AppleTvLibraryPage /></Suspense>} />,
    <Route key="apple-newsroom" path={route("/newsroom")} element={<Suspense fallback={<AppleRouteFallback variant="list" />}><AppleTvNewsroomPage /></Suspense>} />,
    <Route key="apple-account" path={route("/account")} element={<Suspense fallback={<AppleRouteFallback variant="account" />}><AppleTvAccountPage /></Suspense>} />,
    <Route key="apple-list" path={route("/list/:listKey")} element={<Suspense fallback={<AppleRouteFallback variant="list" />}><AppleTvListPage /></Suspense>} />,
    <Route key="apple-person" path={route("/person/:slug/:personId")} element={<Suspense fallback={<AppleRouteFallback variant="person" />}><AppleTvPersonPage /></Suspense>} />,
    <Route key="apple-cast" path={route("/:kind/:slug/:tmdbId/cast-crew")} element={<Suspense fallback={<AppleRouteFallback variant="cast" />}><AppleTvCastCrewPage /></Suspense>} />,
    <Route key="apple-detail" path={route("/:kind/:slug/:tmdbId")} element={<Suspense fallback={<AppleRouteFallback variant="detail" />}><AppleTvDetailPage /></Suspense>} />,
  ];
}

function curatorUserFilmsRouteElements() {
  return [
    <Route key="curator-clean-list" path="/list/:listKey" element={<Suspense fallback={<AppleRouteFallback variant="list" />}><AppleTvListPage /></Suspense>} />,
    <Route key="curator-user-films" path="/:username/films" element={<Suspense fallback={<AppleRouteFallback variant="account" />}><AppleTvUserFilmsProfilePage /></Suspense>} />,
    <Route key="curator-user-films-page" path="/:username/films/page/:page" element={<Suspense fallback={<AppleRouteFallback variant="account" />}><AppleTvUserFilmsProfilePage /></Suspense>} />,
  ];
}

function RoutedContent({ isAuthenticated }) {
  const location = useLocation();
  const applePrimary = isApplePrimaryHost();
  const showAppleSideNav = isAppleSurfacePath(location.pathname);
  const isReelsSurface = location.pathname === "/reels" || location.pathname === "/apple/reels";
  // Detail, Reels, home, library, profile, newsroom, lists, cast & crew:
  // no persistent sidebar — top bar + floating menu (same chrome as home).
  const isImmersiveSurface = isReelsSurface
    || isAppleDetailPath(location.pathname)
    || isAppleHomePath(location.pathname)
    || isAppleTopChromePath(location.pathname);

  useLayoutEffect(() => {
    if (!isAppleDetailPath(location.pathname)) return;
    scrollAppleSurfaceToTop();
  }, [location.pathname]);

  return (
    <RouteErrorBoundary resetKey={location.pathname}>
      {showAppleSideNav ? <AppleTvSideNav suppressMobileTriggers floating={isImmersiveSurface} /> : null}
      <Suspense fallback={<Loading />}>
        <Routes>
          {applePrimary ? (
            <>
              <Route path="/apple/*" element={<AppleLegacyRedirect />} />
              {appleTvRouteElements("")}
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/tea" element={<Suspense fallback={<Loading />}><AppleTvTeaPage /></Suspense>} />
              <Route path="/developer/api" element={<DeveloperApi />} />
              {curatorUserFilmsRouteElements()}
              <Route path="*" element={<Navigate to={appleRoute("")} replace />} />
            </>
          ) : (
            <>
          <Route path="/spotify" element={<Spotify />} />
          <Route path="/youtube" element={<YouTube />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/dev/curator" element={<CuratorDev />} />
          <Route path="/test/movie" element={<MovieDataTest />} />
          <Route path="/admin/monitor" element={<CuratorMonitor />} />
          <Route path="/curator/search" element={<CuratorSearch />} />
          <Route path="/letterboxd" element={<LetterboxdTest />} />
          <Route path="/letterboxd/import" element={<LetterboxdCsvImport />} />
          <Route path="/letterboxd-list" element={<LetterboxdList />} />
          <Route path="/library" element={<Library />} />
          <Route path="/library/:category" element={<Library />} />
          <Route path="/library/:category/:value" element={<Library />} />
          <Route path="/developer/api" element={<DeveloperApi />} />
          <Route path="/tea" element={<Suspense fallback={<Loading />}><AppleTvTeaPage /></Suspense>} />
          <Route path="/mail" element={<GmailClonePage />} />
          <Route path="/mail/:mediaType/:slug/:tmdbId" element={<GmailMoviePage />} />
          {appleTvRouteElements("/apple")}
          <Route path="/" element={<Explore />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/gamer" element={<MoviePair />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/home" element={<HomeScroller />} />
          <Route path="/explore/:tmdbIdSlug" element={<Explore />} />
          <Route path="/movie/:movieId" element={ <MovieDetailPage/>} />
          <Route path="/movie/:movieId" element={<Explore />} />
          <Route path="/search" element={<Home />} />
          <Route path="/search/:movieId" element={<Home />} />
          {curatorUserFilmsRouteElements()}
          <Route path="/lists" element={<ListMenu />} />
          <Route path="/lists/:decade" element={<ListDetail />} />
          <Route
            path="/profile/:userId"
            element={
              isAuthenticated ? <Profile2 /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/profile/:userId/:movieId"
            element={
              isAuthenticated ? <Profile2 /> : <Navigate to="/" replace />
            }
          />
            </>
          )}
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  );
}

const App = () => {
  const { isLoading, error, isAuthenticated, user } = useAuth0();
  const isFullBleedRoute =
    typeof window !== "undefined"
    && (window.location.pathname.startsWith("/developer/api")
      || window.location.pathname === "/tea"
      || window.location.pathname === "/apple/tea"
      || isAppleSurfacePath(window.location.pathname)
      || window.location.pathname.startsWith("/mail"));

  const metadataOptions = {
    Genres: ["Action", "Drama", "Comedy"],
    Decades: ["1990", "2000", "2010"],
    Directors: ["Steven Spielberg", "Christopher Nolan"],
    Streaming: ["Netflix", "Hulu", "Amazon Prime"],
  };

  useEffect(() => {
    initGA();
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Keep Apple light/dark in sync with System (and Account Appearance) for the
  // whole session — do not tie this to SideNav mount/unmount.
  useEffect(() => {
    applyAppleTheme(ensureAppleThemeDefault());
    return subscribeAppleSystemTheme();
  }, []);

  // SSR/SEO prerender blocks sit in index.html for crawlers; remove once React mounts.
  useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    document
      .querySelectorAll(
        "#curator-seo-prerender, #curator-seo-list, #curator-seo-search-snippet, "
        + "#curator-seo-plain, .curator-seo-search-snippet, noscript",
      )
      .forEach((node) => node.remove());
  }, []);

  if (error) {
    return <div>Oops... {error.message}</div>;
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Router history={history}>
      <AnalyticsRouter />
      <div id="app" className="d-flex flex-column h-100">
        <NavBar />
        <Container
          fluid={isFullBleedRoute}
          className={isFullBleedRoute ? "flex-grow-1 px-0" : "flex-grow-1 no-scroll-fullscreen"}
        >
          <RoutedContent isAuthenticated={isAuthenticated} />
        </Container>
        {!isFullBleedRoute && !isAuthenticated ? <FooterUpsell /> : null}
        {!isFullBleedRoute ? <Footer /> : null}
        <Analytics />
        <SpeedInsights />
      </div>
    </Router>
  );
};

export default App;